import React, { useEffect, useState, useCallback } from 'react';
import { CheckCircle2, XCircle, Info, BookOpen, Wifi, WifiOff } from 'lucide-react';

// --------------------------------------------------------------------------
// Dynamic Island-style notification.
//
// Apple's Dynamic Island is the pill at the top of iPhone 14 Pro+. We can't
// actually render INSIDE it from a webview (only iOS itself owns that), but
// we can mimic it: a black pill that sits centered at the top of the screen,
// expands when there's something to say, and collapses when idle. On devices
// that have a real Dynamic Island, we sit just below it so we don't fight.
//
// API (anywhere in the app):
//
//   import { dynamicIsland } from '@/components/shared/DynamicIsland';
//   dynamicIsland.show({ kind: 'success', title: 'Logged in', subtitle: 'Welcome back, Jane' });
//   dynamicIsland.hide();
//
// Mount <DynamicIsland /> once near the root (App.tsx) and that's it.
// --------------------------------------------------------------------------

export type IslandKind =
  | 'success'
  | 'error'
  | 'info'
  | 'reading'
  | 'online'
  | 'offline';

export interface IslandPayload {
  kind: IslandKind;
  title: string;
  subtitle?: string;
  /** Auto-hide after N ms. 0 = sticky. Default 3500. */
  durationMs?: number;
  /** Optional click handler — when set, the pill becomes tappable. */
  onClick?: () => void;
}

type Listener = (payload: IslandPayload | null) => void;

// Tiny event bus so any component / store can fire notifications without
// React context plumbing.
const listeners = new Set<Listener>();
let currentTimeout: ReturnType<typeof setTimeout> | null = null;

export const dynamicIsland = {
  show(payload: IslandPayload) {
    if (currentTimeout) {
      clearTimeout(currentTimeout);
      currentTimeout = null;
    }
    listeners.forEach((l) => l(payload));
    const dur = payload.durationMs ?? 3500;
    if (dur > 0) {
      currentTimeout = setTimeout(() => {
        listeners.forEach((l) => l(null));
        currentTimeout = null;
      }, dur);
    }
  },
  hide() {
    if (currentTimeout) {
      clearTimeout(currentTimeout);
      currentTimeout = null;
    }
    listeners.forEach((l) => l(null));
  },
};

const ICON_MAP: Record<IslandKind, React.ReactNode> = {
  success: <CheckCircle2 className="w-4 h-4 text-green-400" />,
  error:   <XCircle className="w-4 h-4 text-red-400" />,
  info:    <Info className="w-4 h-4 text-blue-300" />,
  reading: <BookOpen className="w-4 h-4 text-[#fdce03]" />,
  online:  <Wifi className="w-4 h-4 text-green-400" />,
  offline: <WifiOff className="w-4 h-4 text-red-400" />,
};

const DynamicIsland: React.FC = () => {
  const [payload, setPayload] = useState<IslandPayload | null>(null);

  useEffect(() => {
    const listener: Listener = (next) => setPayload(next);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const handleClick = useCallback(() => {
    if (payload?.onClick) payload.onClick();
  }, [payload]);

  const visible = payload !== null;

  return (
    <>
      <style>{islandStyles}</style>
      <div
        className="dynamic-island-wrap"
        // Stops the pill from blocking taps on the rest of the UI when hidden
        style={{ pointerEvents: visible ? 'auto' : 'none' }}
        aria-live="polite"
        role="status"
      >
        <button
          type="button"
          onClick={handleClick}
          disabled={!visible || !payload?.onClick}
          className={`dynamic-island ${visible ? 'is-open' : ''} ${
            payload?.onClick ? 'is-clickable' : ''
          }`}
        >
          {payload && (
            <span className="dynamic-island-content">
              <span className="dynamic-island-icon">{ICON_MAP[payload.kind]}</span>
              <span className="dynamic-island-text">
                <span className="dynamic-island-title">{payload.title}</span>
                {payload.subtitle && (
                  <span className="dynamic-island-subtitle">{payload.subtitle}</span>
                )}
              </span>
            </span>
          )}
        </button>
      </div>
    </>
  );
};

const islandStyles = `
  .dynamic-island-wrap {
    position: fixed;
    top: max(env(safe-area-inset-top, 0px), 8px);
    left: 0;
    right: 0;
    z-index: 9999;
    display: flex;
    justify-content: center;
    /* On iPhones with a real Dynamic Island, the safe-area-inset-top already
       pushes content below the pill. On other devices, the 8px fallback gives
       a comfy gap from the very edge. */
  }
  .dynamic-island {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    width: 124px;
    padding: 0;
    border: none;
    background: #000;
    color: #fff;
    border-radius: 999px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.04) inset;
    transition: width 0.42s cubic-bezier(0.4, 0.8, 0.2, 1.2),
                height 0.42s cubic-bezier(0.4, 0.8, 0.2, 1.2),
                opacity 0.25s ease;
    overflow: hidden;
    opacity: 0;
    cursor: default;
  }
  .dynamic-island.is-open {
    width: min(360px, calc(100vw - 32px));
    height: 56px;
    opacity: 1;
  }
  .dynamic-island.is-clickable.is-open {
    cursor: pointer;
  }
  .dynamic-island.is-clickable.is-open:active {
    transform: scale(0.97);
  }
  .dynamic-island-content {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 18px;
    width: 100%;
    animation: dynamic-island-fade-in 0.32s ease-out 0.1s both;
  }
  .dynamic-island-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: rgba(255,255,255,0.08);
    border-radius: 999px;
  }
  .dynamic-island-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    overflow: hidden;
    text-align: left;
  }
  .dynamic-island-title {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 280px;
  }
  .dynamic-island-subtitle {
    font-size: 11px;
    color: rgba(255,255,255,0.6);
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 280px;
  }
  @keyframes dynamic-island-fade-in {
    0%   { opacity: 0; transform: translateY(2px); }
    100% { opacity: 1; transform: translateY(0);   }
  }
`;

export default DynamicIsland;
